import {
  AnimationComponent,
  Box,
  Calendar1Icon,
  CircularProgress,
  DeleteIcon,
  Divider,
  DownloadIcon,
  Edit1Icon,
  GKButton,
  GKTypography,
  Grid2,
  IconButton,
  SendIcon,
  SendNotificationIcon,
  Show,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { SingleModelPortfolioRebalncesInterface } from "interfaces/EquityBasket";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { handleDownloadFile, matchDate, refetchQuery } from "utils/helpers";
import SendRecommendationNotification from "../EquityBasketStockRecommendation/SendRecommendationNotification";
import RebalanceModal from "../ModelPortfolioVersion/RebalanceModal";

const Rebalances = () => {
  const { id } = useParams();

  const [tabIndex, setTabIndex] = useState(0);

  const [openModelToRebalance, setOpenModelToRebalance] = useState(false);

  const [
    showRecommendationNotificationModal,
    setShowRecommendationNotificationModal,
  ] = useState(false);

  const [modelPortfolioId, setModelPortfolioId] = useState(null);

  const [selectedRebalanceId, setSelectedRebalanceId] = useState(null);

  const [isRebalanceEditModal, setIsRebalanceEditModal] = useState(false);

  const { data, isFetching } = useFetchQuery<{
    data: { data: SingleModelPortfolioRebalncesInterface[] };
  }>({
    key: ["GET_REBALANCES", tabIndex],
    route: `/model_portfolio/${id}/version/rebalance_list/?is_draft=${tabIndex === 0}`,
  });

  const { data: rebalanceDetails } = useFetchQuery<{
    data: {
      data: {
        rebalanceFrequency: string;
        lastRebalanceDate: string;
        nextRebalanceDate: string;
        totlaRebalanceDone: number;
      };
    };
  }>({
    key: ["GET_REBALANCES_DETAILS"],
    route: `/model_portfolio/${id}/version/primary_rebalance_summary/`,
  });

  const { isLoading, mutate } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(
        `/model_portfolio/${id}/version/${data?.version}/rebalance/`,
        data,
      ),
    onSuccess: () => {
      refetchQuery("GET_REBALANCES");
      refetchQuery("GET_REBALANCES_DETAILS");
    },
  });

  const { isLoading: deleteLoading, mutate: handleDeleteRebalances } =
    useMutation({
      mutationFn: (data: any) =>
        axiosInstance.delete(
          `/model_portfolio/${id}/version/${data?.version}/rebalance/`,
          {
            data: {
              rebalanceId: data?.rebalanceId,
            },
          },
        ),
      onSuccess: () => {
        refetchQuery("GET_REBALANCES");
        refetchQuery("GET_REBALANCES_DETAILS");
      },
    });

  return (
    <AnimationComponent>
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Show.When
            isTrue={
              !isEmpty(rebalanceDetails?.data?.data?.rebalanceFrequency) ||
              !isEmpty(rebalanceDetails?.data?.data?.lastRebalanceDate) ||
              !isEmpty(rebalanceDetails?.data?.data?.nextRebalanceDate)
            }
            showNodata
          >
            <TableContainer sx={{ border: "none" }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Rebalance Frequency
                      </Typography>
                      <Typography fontWeight={600}>
                        {rebalanceDetails?.data?.data?.rebalanceFrequency ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Last Rebalance Date
                      </Typography>
                      <Typography fontWeight={600}>
                        {matchDate(
                          rebalanceDetails?.data?.data?.lastRebalanceDate,
                        ) || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Next Rebalance Date
                      </Typography>
                      <Typography fontWeight={600}>
                        {matchDate(
                          rebalanceDetails?.data?.data?.nextRebalanceDate,
                        ) || "-"}
                      </Typography>
                    </TableCell>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <TableCell
                        key={index}
                        style={{
                          backgroundColor: "#F8F8F8",
                          padding: 24,
                        }}
                      />
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Show.When>
        </Grid2>
        <Grid2 size={12}>
          <Tabs value={tabIndex} onChange={(e, value) => setTabIndex(value)}>
            <Tab label="Draft" />
            <Tab label="Rebalance" />
          </Tabs>
          <Divider />
        </Grid2>
        <Show.When
          isTrue={data?.data?.data?.length !== 0}
          showNodata
          textContent={
            tabIndex === 0 ? "No drafts to show" : "No rebalance to show"
          }
        >
          <Grid2 size={9}>
            {(isFetching ? Array.from({ length: 5 }) : data?.data?.data)?.map(
              (dataItem: any, index) => {
                return (
                  <Grid2
                    container
                    key={index}
                    sx={{
                      borderBottom:
                        index + 1 === data?.data?.data?.length
                          ? "none"
                          : "1px solid #E6E6E6",
                      padding: 2,
                    }}
                  >
                    <Grid2 size={6}>
                      <GKTypography
                        fontSize={16}
                        fontWeight={600}
                        color="#1A1A1A"
                        loading={isFetching}
                      >
                        {dataItem?.name}
                      </GKTypography>
                      <Box display={"flex"} gap={0.2} alignItems={"center"}>
                        {isFetching && <Calendar1Icon />}
                        <GKTypography loading={isFetching} color="#1A1A1A">
                          Creation Date:
                        </GKTypography>
                        <GKTypography
                          fontSize={16}
                          fontWeight={600}
                          color="#1A1A1A"
                          loading={isFetching}
                        >
                          {matchDate(dataItem?.date)}
                        </GKTypography>
                      </Box>
                    </Grid2>
                    <Grid2
                      size={6}
                      textAlign={"right"}
                      container
                      justifyContent={"end"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <Show.When isTrue={!dataItem?.isDraft}>
                        <GKButton
                          variant="outlined"
                          loading={isFetching}
                          color="dark"
                          startIcon={<SendNotificationIcon />}
                          onClick={() => {
                            setSelectedRebalanceId(dataItem?.id);
                            setShowRecommendationNotificationModal(true);
                          }}
                        >
                          Send Notification
                        </GKButton>
                      </Show.When>

                      <Show.When isTrue={dataItem?.isDraft}>
                        <GKButton
                          variant="outlined"
                          loading={isFetching}
                          color="dark"
                          startIcon={<Edit1Icon />}
                          onClick={() => {
                            setModelPortfolioId(dataItem?.version);
                            setSelectedRebalanceId(dataItem?.id);
                            setOpenModelToRebalance(true);
                            setIsRebalanceEditModal(true);
                          }}
                        >
                          Edit
                        </GKButton>
                      </Show.When>
                      <Show.When isTrue={dataItem?.isDraft}>
                        <GKButton
                          loading={isLoading}
                          variant="contained"
                          startIcon={<SendIcon />}
                          onClick={() => {
                            mutate({
                              version: dataItem?.version,
                              cash: Number(dataItem?.cash),
                              date: dayjs(dataItem?.date).format("YYYY-MM-DD"),
                              nextRebalanceDate: dayjs(
                                dataItem?.nextRebalanceDate,
                              ).format("YYYY-MM-DD"),
                              transactionType: dataItem?.transactionType,
                              remarks: dataItem?.remarks,
                              isDraft: false,
                              rebalancedVersion: {
                                allocItem: dataItem?.draftRebalanceTransactions,
                              },
                            });
                          }}
                        >
                          Publish
                        </GKButton>
                      </Show.When>
                      <Show.When isTrue={!dataItem?.isDraft}>
                        <GKButton
                          variant="contained"
                          loading={isFetching}
                          startIcon={<DownloadIcon />}
                          onClick={() => {
                            handleDownloadFile({
                              link: dataItem?.reportFile,
                              name: `${dataItem?.name}_Model_Portfolio_report`,
                            });
                          }}
                        >
                          Download
                        </GKButton>
                      </Show.When>
                      <Show.When isTrue={dataItem?.isDraft}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedRebalanceId(dataItem?.id);
                            handleDeleteRebalances({
                              version: dataItem?.version,
                              rebalanceId: dataItem?.id,
                            });
                          }}
                        >
                          {dataItem?.id === selectedRebalanceId &&
                          deleteLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon width={20} height={20} />
                          )}
                        </IconButton>
                      </Show.When>
                    </Grid2>
                  </Grid2>
                );
              },
            )}
          </Grid2>
        </Show.When>
      </Grid2>
      <Show.When isTrue={openModelToRebalance}>
        <RebalanceModal
          setOpen={setOpenModelToRebalance}
          open={openModelToRebalance}
          selectedId={modelPortfolioId}
          isEditModal={isRebalanceEditModal}
          rebalanceId={selectedRebalanceId}
        />
      </Show.When>
      <Show.When isTrue={showRecommendationNotificationModal}>
        <SendRecommendationNotification
          open={showRecommendationNotificationModal}
          selectedRecommendation={selectedRebalanceId}
          setOpen={setShowRecommendationNotificationModal}
          isRebalanceModal
        />
      </Show.When>
    </AnimationComponent>
  );
};

export default Rebalances;
